import { useState, useEffect } from 'react';
import { fetchInsuranceCompanies, fetchAgencyInsuranceCompanies } from '../../../services/api/InsuranceService';
import { fetchDocumentTypesAccess } from '../../../services/api/DocumentTypeService';
import useRequireAuth from '../../useRequireAuth';

/**
 * 搭載文書検索画面用マスターデータ取得フック
 *
 * 画面マウント時に保険会社一覧・アクセス可能文書種別をAPIから取得する。
 * 代理店ユーザーの場合は approved_document_types 付きの代理店保険会社一覧も取得する。
 *
 * @returns {Object} マスターデータと状態
 * @returns {Object} returns.userInfo - ログインユーザー情報
 * @returns {Array} returns.insuranceCompanies - 全保険会社一覧
 * @returns {Array} returns.agencyInsuranceCompanies - 代理店保険会社一覧（approved_document_types付き）
 * @returns {Array} returns.documentTypesAccess - アクセス可能な文書種別一覧
 * @returns {boolean} returns.isMasterLoading - マスターデータ取得中フラグ
 * @returns {Error|null} returns.masterError - マスターデータ取得エラー
 */
const useMasterData = () => {
  const userInfo = useRequireAuth();

  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [agencyInsuranceCompanies, setAgencyInsuranceCompanies] = useState([]);
  const [documentTypesAccess, setDocumentTypesAccess] = useState([]);
  const [isMasterLoading, setIsMasterLoading] = useState(false);
  const [masterError, setMasterError] = useState(null);

  useEffect(() => {
    // 未認証時は空オブジェクトが返るため、キーの有無でフェッチをガードする
    if (!userInfo || Object.keys(userInfo).length === 0) return;

    const load = async () => {
      setIsMasterLoading(true);
      try {
        // 保険会社一覧は必ず取得（失敗時は masterError をセット）
        const companies = await fetchInsuranceCompanies();
        setInsuranceCompanies(companies || []);

        // アクセス可能文書種別は独立取得（失敗しても保険会社表示は止めない）
        try {
          const accessTypes = await fetchDocumentTypesAccess();
          setDocumentTypesAccess(accessTypes || []);
        } catch (err) {
          // documentTypesAccess は空のまま（フィルタリングは全社表示にフォールバック）
          console.warn('[useMasterData] documentTypesAccess の取得に失敗しました。全文書種別を表示します。', err);
        }

        if (!userInfo.is_admin) {
          try {
            const agencyCompanies = await fetchAgencyInsuranceCompanies(userInfo.agency_id);
            // 代理店 API は名称を省略して返す場合があるため、全社マスター(companies)から名称を補完する
            const enriched = (agencyCompanies || []).map(agencyCompany => {
              const full = (companies || []).find(c => c.id === agencyCompany.id) || {};
              return {
                id: agencyCompany.id,
                name: full.name || agencyCompany.name,
                name_kana: full.name_kana || agencyCompany.name_kana,
                insurance_type: full.insurance_type ?? agencyCompany.insurance_type,
                approved_document_types: agencyCompany.approved_document_types || [],
              };
            });
            setAgencyInsuranceCompanies(enriched);
          } catch (err) {
            // agencyInsuranceCompanies は空のまま（フィルタリングは全社表示にフォールバック）
            console.warn('[useMasterData] agencyInsuranceCompanies の取得に失敗しました。全社表示にフォールバックします。', err);
          }
        }
      } catch (error) {
        setMasterError(error);
      } finally {
        setIsMasterLoading(false);
      }
    };
    load();
  }, [userInfo]);

  return {
    userInfo,
    insuranceCompanies,
    agencyInsuranceCompanies,
    documentTypesAccess,
    isMasterLoading,
    masterError,
  };
};

export default useMasterData;
